// Plan 27 Phase 3 — vendor-neutral auth client.
//
// Replaces the @supabase/supabase-js auth surface for the dashboard UI.
// Calls our own /api/auth/* endpoints (delivered by services/auth/auth_service.py
// and dashboard/auth_routes.py).
//
// Surface:
//   signIn(email, password)        -> AuthResult
//   signUp(email, password)        -> AuthResult
//   signOut()                      -> void
//   getSession()                   -> Session | null
//   onAuthStateChange(callback)    -> unsubscribe()
//
// JWT lifecycle:
//   * Stored in localStorage under 'qyngent.access_token'
//   * Read on getSession() — expired tokens are auto-cleared
//   * apiFetch picks up the token from localStorage on every request

import { apiFetch } from '@/lib/api'

const TOKEN_KEY = 'qyngent.access_token'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface User {
  id: string
  email?: string
  role?: string
}

export interface Session {
  user: User
  accessToken: string
  expiresAt: number  // unix seconds
}

export interface AuthResult {
  user: User
  session: Session
}

export type AuthEvent = 'SIGNED_IN' | 'SIGNED_OUT' | 'TOKEN_REFRESHED'

export type AuthChangeCallback = (event: AuthEvent, session: Session | null) => void

interface AuthApiResponse {
  user_id: string
  access_token: string
  token_type: string
}

// ---------------------------------------------------------------------------
// Internal: JWT decode (signature NOT verified — backend does that)
// ---------------------------------------------------------------------------

interface JwtClaims {
  sub: string
  exp: number
  role?: string
  iss?: string
}

function decodeJwtUnverified(token: string): JwtClaims | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    // base64url decode
    const padded = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padding = '='.repeat((4 - (padded.length % 4)) % 4)
    const decoded = atob(padded + padding)
    return JSON.parse(decoded) as JwtClaims
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// Auth-state subscribers
// ---------------------------------------------------------------------------

const subscribers = new Set<AuthChangeCallback>()

function emit(event: AuthEvent, session: Session | null): void {
  // setTimeout(0) so subscribers fire after the calling function's
  // local state updates have settled.
  setTimeout(() => {
    for (const cb of subscribers) {
      try {
        cb(event, session)
      } catch (err) {
        // Subscriber errors must not break other subscribers
        console.error('[auth] onAuthStateChange callback error:', err)
      }
    }
  }, 0)
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Sign in with email + password. Stores the JWT on success and notifies
 * onAuthStateChange subscribers with SIGNED_IN.
 */
export async function signIn(email: string, password: string): Promise<AuthResult> {
  const resp = await apiFetch<AuthApiResponse>('/api/auth/sign-in', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  })
  const session = persistAndBuildSession(resp)
  emit('SIGNED_IN', session)
  return { user: session.user, session }
}

/**
 * Create a new account. Email verification is required: signup does NOT issue a
 * session — the backend emails a 6-digit OTP. Returns a verification-required
 * marker; call verifyEmailOtp() with the code to finish (which signs the user in).
 */
export interface VerificationRequired {
  verificationRequired: true
  email: string
}

export async function signUp(email: string, password: string): Promise<VerificationRequired> {
  const resp = await apiFetch<{ verification_required?: boolean; email?: string }>(
    '/api/auth/sign-up',
    { method: 'POST', body: JSON.stringify({ email, password }) },
  )
  return { verificationRequired: true, email: resp.email ?? email }
}

/**
 * Verify the signup email OTP. On success the backend issues a JWT (auto
 * sign-in); we persist it and emit SIGNED_IN so the app routes onward.
 */
export async function verifyEmailOtp(email: string, otp: string): Promise<AuthResult> {
  const resp = await apiFetch<AuthApiResponse>('/api/auth/verify-email-otp', {
    method: 'POST',
    body: JSON.stringify({ email, otp }),
  })
  const session = persistAndBuildSession(resp)
  emit('SIGNED_IN', session)
  return { user: session.user, session }
}

/** Resend the signup verification OTP. Always resolves (generic server response). */
export async function resendVerification(email: string): Promise<void> {
  await apiFetch('/api/auth/resend-verification', {
    method: 'POST',
    body: JSON.stringify({ email }),
  })
}

/**
 * Sign the user out. Clears the stored JWT and notifies subscribers.
 * Idempotent: safe to call when already signed out.
 */
export async function signOut(): Promise<void> {
  const wasSignedIn = localStorage.getItem(TOKEN_KEY) !== null
  localStorage.removeItem(TOKEN_KEY)
  if (wasSignedIn) {
    emit('SIGNED_OUT', null)
  } else {
    emit('SIGNED_OUT', null)  // still notify so UI can re-sync
  }
}

/**
 * Return the current session if a non-expired token is stored, else null.
 * Expired tokens are cleared as a side-effect.
 */
export async function getSession(): Promise<Session | null> {
  const token = localStorage.getItem(TOKEN_KEY)
  if (!token) return null

  const claims = decodeJwtUnverified(token)
  if (!claims || !claims.sub || !claims.exp) {
    // Malformed — clear it
    localStorage.removeItem(TOKEN_KEY)
    return null
  }

  const nowSec = Math.floor(Date.now() / 1000)
  if (claims.exp <= nowSec) {
    // Expired — clear so we don't keep sending a dead token
    localStorage.removeItem(TOKEN_KEY)
    return null
  }

  return {
    user: { id: claims.sub, role: claims.role },
    accessToken: token,
    expiresAt: claims.exp,
  }
}

/**
 * Subscribe to auth state changes. Returns an unsubscribe function.
 *
 *   const unsub = onAuthStateChange((event, session) => { ... })
 *   // ...later
 *   unsub()
 */
export function onAuthStateChange(callback: AuthChangeCallback): () => void {
  subscribers.add(callback)
  return () => {
    subscribers.delete(callback)
  }
}

/**
 * Read the current access token (or null). Used by apiFetch to attach
 * Authorization: Bearer <token> on every authed request.
 */
export function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function persistAndBuildSession(resp: AuthApiResponse): Session {
  localStorage.setItem(TOKEN_KEY, resp.access_token)
  // A fresh token clears the apiFetch 401-storm suppression flag so
  // a subsequent expiry of THIS token still fires the handler.
  try {
    // Dispatch a no-op event apiFetch listens for to reset its flag.
    window.dispatchEvent(new CustomEvent('qyngent:session-restored'))
  } catch {
    /* swallow */
  }
  const claims = decodeJwtUnverified(resp.access_token)
  return {
    user: { id: resp.user_id, role: claims?.role },
    accessToken: resp.access_token,
    expiresAt: claims?.exp ?? Math.floor(Date.now() / 1000) + 3600,
  }
}
